import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setWhatsAppObservationsFilters, toggleWhatsAppFilters } from '../../actions/index';
import { _ } from '../../functions/translator';
import { makeStyles } from '@material-ui/core/styles';
import { 
    Paper, 
    TextField, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Button, 
    IconButton,
    Typography
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles((theme) => ({
    filterContainer: {
        position: 'fixed',
        top: 80,
        right: 20,
        width: '350px',
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        zIndex: 999,
        backgroundColor: 'var(--dark)',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        '@media only screen and (max-width: 1366px)': {
            top: '140px',
            maxHeight: 'calc(100vh - 200px)',
        },
    },
    filterHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        borderBottom: '1px solid #444',
        backgroundColor: '#1a1a1a',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
    },
    filterTitle: {
        color: '#fff',
        fontSize: '18px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    filterContent: {
        padding: '16px',
    },
    formControl: {
        marginBottom: '16px',
        width: '100%',
        '& .MuiInputLabel-root': {
            color: '#999',
        },
        '& .MuiOutlinedInput-root': {
            color: '#fff',
        },
        '& .MuiSelect-icon': {
            color: '#999',
        },
        '& .MuiInputBase-input': {
            color: '#fff',
        },
    },
    selected: {
        backgroundColor: '#3f51b5 !important',
        color: 'white',
    },
    buttonGroup: {
        display: 'flex',
        gap: '8px',
        marginTop: '16px',
    },
    clearButton: {
        backgroundColor: '#444',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#555',
        },
    },
    applyButton: {
        backgroundColor: '#25D366',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#1fa855',
        },
    },
    dateRange: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
}));

const WhatsAppFilters = ({ 
    filters, 
    whatsappObservations, 
    setWhatsAppObservationsFilters,
    toggleWhatsAppFilters,
    whatsappObservationsPopup
}) => {
    const classes = useStyles();

    // Memoize unique observers - only recalculate when observations change
    const uniqueObservers = useMemo(() => {
        const observers = new Set();
        const observations = whatsappObservations?.observations || {};
        
        for (const obs of Object.values(observations)) {
            if (obs.observation) {
                const firstName = obs.observation.user_first_name || '';
                const lastName = obs.observation.user_last_name || '';
                const phone = obs.observation.user_phone || '';
                const fullName = `${firstName} ${lastName}`.trim();
                if (fullName) {
                    observers.add(fullName);
                } else if (phone) {
                    observers.add(phone);
                }
            }
        }
        return Array.from(observers).sort();
    }, [whatsappObservations?.observations]);

    // Memoize unique extra field values - only recalculate when observations change
    const uniqueExtraFields = useMemo(() => {
        const extraFieldsMap = {};
        const observations = whatsappObservations?.observations || {};
        
        for (const obs of Object.values(observations)) {
            if (obs.observation && obs.observation.extra_fields) {
                for (const [key, value] of Object.entries(obs.observation.extra_fields)) {
                    if (!extraFieldsMap[key]) {
                        extraFieldsMap[key] = new Set();
                    }
                    if (value) {
                        extraFieldsMap[key].add(value);
                    }
                }
            }
        }
        
        // Convert Sets to sorted arrays
        const result = {};
        for (const [key, valueSet] of Object.entries(extraFieldsMap)) {
            // Filter out values with multiple departments (containing commas)
            const filteredValues = Array.from(valueSet).filter(value => !value.includes(','));
            
            // Sort numerically if values are numbers, otherwise alphabetically
            result[key] = filteredValues.sort((a, b) => {
                const numA = Number(a);
                const numB = Number(b);
                // If both are valid numbers, sort numerically
                if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB;
                }
                // Otherwise, sort alphabetically
                return a.localeCompare(b);
            });
        }
        return result;
    }, [whatsappObservations?.observations]);

    const handleFilterChange = (field, value) => {
        setWhatsAppObservationsFilters({ [field]: value });
    };

    const handleExtraFieldChange = (fieldName, value) => {
        setWhatsAppObservationsFilters({
            extraFields: {
                ...filters.extraFields,
                [fieldName]: value
            }
        });
    };

    const handleClearFilters = () => {
        setWhatsAppObservationsFilters({
            resolved: [],
            observedBy: [],
            startDate: null,
            endDate: null,
            search: '',
            extraFields: {}
        });
    };

    if (!filters.showFilters || whatsappObservationsPopup.show) return null;

    return (
        <Paper className={classes.filterContainer} elevation={8}>
            <div className={classes.filterHeader}>
                <Typography className={classes.filterTitle}>
                    <FilterListIcon />
                    {_('Filtros')}
                </Typography>
                <IconButton size="small" onClick={toggleWhatsAppFilters} style={{ color: '#fff' }}>
                    <CloseIcon />
                </IconButton>
            </div>
            
            <div className={classes.filterContent}>
                {/* Search */}
                <FormControl className={classes.formControl} variant="outlined" size="small">
                    <TextField
                        label={_('Buscar')}
                        variant="outlined"
                        size="small"
                        value={filters.search || ''}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon style={{ color: '#999', marginRight: '8px' }} />,
                            endAdornment: filters.search && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleFilterChange('search', '')}
                                    style={{ color: '#999' }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                </FormControl>

                {/* Resolved Status */}
                <FormControl className={classes.formControl} variant="outlined" size="small">
                    <InputLabel>{_('Estado')}</InputLabel>
                    <Select
                        multiple
                        value={filters.resolved || []}
                        onChange={(e) => handleFilterChange('resolved', e.target.value)}
                        label={_('Estado')}
                        renderValue={(selected) => selected.map(value => 
                            value === 'resolved' ? _('Resuelta') : _('Sin Resolver')
                        ).join(', ')}
                        MenuProps={{
                            MenuListProps: {
                                disablePadding: true,
                            },
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                    top: '0px',
                                    position: 'fixed',
                                },
                            },
                            getContentAnchorEl: null,
                        }}
                    >
                        <MenuItem value="resolved" classes={{ selected: classes.selected }}>
                            {_('Resuelta')}
                        </MenuItem>
                        <MenuItem value="unresolved" classes={{ selected: classes.selected }}>
                            {_('Sin Resolver')}
                        </MenuItem>
                    </Select>
                </FormControl>

                {/* Observed By */}
                {uniqueObservers.length > 0 && (
                    <FormControl className={classes.formControl} variant="outlined" size="small">
                        <InputLabel>{_('Observado por')}</InputLabel>
                        <Select
                            multiple
                            value={filters.observedBy || []}
                            onChange={(e) => handleFilterChange('observedBy', e.target.value)}
                            label={_('Observado por')}
                            renderValue={(selected) => selected.join(', ')}
                            MenuProps={{
                                MenuListProps: {
                                    disablePadding: true,
                                },
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        top: '0px',
                                        position: 'fixed',
                                    },
                                },
                                getContentAnchorEl: null,
                            }}
                        >
                            {uniqueObservers.map((observer) => (
                                <MenuItem key={observer} value={observer} classes={{ selected: classes.selected }}>
                                    {observer}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Date Range */}
                <div className={classes.dateRange}>
                    <FormControl className={classes.formControl} variant="outlined" size="small" style={{ marginBottom: 0 }}>
                        <TextField
                            label={_('Fecha inicio')}
                            type="date"
                            variant="outlined"
                            size="small"
                            value={filters.startDate || ''}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl} variant="outlined" size="small" style={{ marginBottom: 0 }}>
                        <TextField
                            label={_('Fecha fin')}
                            type="date"
                            variant="outlined"
                            size="small"
                            value={filters.endDate || ''}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </FormControl>
                </div>

                {/* Extra Fields */}
                {Object.entries(uniqueExtraFields).map(([fieldName, values]) => (
                    <FormControl key={fieldName} className={classes.formControl} variant="outlined" size="small">
                        <InputLabel>{fieldName}</InputLabel>
                        <Select
                            multiple
                            value={filters.extraFields[fieldName] || []}
                            onChange={(e) => handleExtraFieldChange(fieldName, e.target.value)}
                            label={fieldName}
                            renderValue={(selected) => selected.join(', ')}
                            MenuProps={{
                                MenuListProps: {
                                    disablePadding: true,
                                },
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        top: '0px',
                                        position: 'fixed',
                                    },
                                },
                                getContentAnchorEl: null,
                            }}
                        >
                            {values.map((value) => (
                                <MenuItem key={value} value={value} classes={{ selected: classes.selected }}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ))}

                {/* Action Buttons */}
                <div className={classes.buttonGroup}>
                    <Button
                        variant="contained"
                        className={classes.clearButton}
                        onClick={handleClearFilters}
                        fullWidth
                    >
                        {_('Limpiar')}
                    </Button>
                </div>
            </div>
        </Paper>
    );
};

const mapStateToProps = state => ({
    filters: state.whatsappObservations.filters,
    whatsappObservations: state.whatsappObservations,
    whatsappObservationsPopup: state.whatsappObservations.popup,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    setWhatsAppObservationsFilters,
    toggleWhatsAppFilters,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(WhatsAppFilters);


